/**
 * document_generator_controller.js — the public invoice and quote generators
 * (/tools/invoice-generator and /tools/quote-generator).
 *
 * One controller for both. The two pages differ in their copy and in one date
 * field, none of which this file knows about: it posts whatever the form holds
 * to whatever URL the template gave it. Nothing here forks on document kind.
 *
 * Drives four things over one form: a live preview of the actual document, an
 * AI assistant that fills the form from plain English, the line-item rows, and
 * the logo.
 *
 * The preview is rendered SERVER-side from the same template the PDF is made
 * from, debounced and dropped into an iframe. That is deliberate. A preview
 * built in JS would be a second implementation of the document and would drift
 * from the PDF; here what the visitor watches take shape IS the PDF, and every
 * number in it was computed by DraftDocumentService, not by this file.
 *
 * The logo never becomes an upload. The browser reads the chosen file into a
 * data URI and puts it in a hidden field, so it rides the same POST as the
 * rest of the form and is gone the moment the PDF is built.
 *
 * The form itself is an ordinary POST with data-turbo="false" on it, so the
 * page still works with JavaScript off: you lose the preview, the logo and the
 * assistant, you keep the document.
 */

import { Controller } from "@hotwired/stimulus";

// The preview is THROTTLED, not debounced. A debounce only fires once typing
// stops, which is why the document used to sit still until you paused. This
// renders on the first keystroke and then keeps rendering while you type, at
// most this often.
const PREVIEW_THROTTLE_MS = 120;
const A4_WIDTH_PX = 794;   // 210mm at 96dpi, the width the document renders at
// A short invoice should not be shown on most of a blank page. The paper is
// sized to its content instead, with this as the floor so an empty document
// still reads as a sheet of paper rather than a strip.
const MIN_PAPER_PX = 560;
// Mirrors MAX_LOGO_CHARS in application/schemas/draft_document.py. Checked here
// too so an oversized image is refused before it is posted, with a message that
// names the actual problem instead of a validation error after the round trip.
const MAX_LOGO_CHARS = 400000;
// The required fields the progress counter tracks, matching REQUIRED on the
// service. `gst_registered` is required too but is a select that always holds a
// value, so counting it would credit the visitor for doing nothing.
const REQUIRED_FIELDS = ["business_name", "client_name", "document_number", "issue_date"];

export default class extends Controller {
    static targets = ["form", "rows", "rowTemplate", "frame", "stage", "paper",
                      "aiInput", "aiSend", "aiLog", "aiStatus", "pane",
                      "logoField", "logoLabel", "logoClear",
                      "progressBar", "progressText", "accountNote"];
    static values = { previewUrl: String, aiUrl: String };

    connect() {
        this._session = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID() : String(Date.now());
        this._history = [];
        this._timer = null;
        this._ticket = 0;
        // Turbo can reconnect this controller onto a fresh iframe, so the
        // "styles are in there already" flag has to reset with it.
        this._frameReady = false;
        this._onResize = () => this.fitPreview();
        window.addEventListener("resize", this._onResize);
        this.fitPreview();
        this.updateProgress();
        this.refreshPreview();
    }

    disconnect() {
        window.removeEventListener("resize", this._onResize);
        clearTimeout(this._timer);
    }

    // --- preview -----------------------------------------------------------

    /**
     * Keep the document moving while they type.
     *
     * Leading edge first, so the very first keystroke shows up immediately,
     * then at most one render per throttle window with a trailing render to
     * catch whatever was typed inside the last one. The visitor never waits for
     * a pause, and the server never sees one request per keypress.
     */
    schedulePreview() {
        this.updateProgress();

        const since = Date.now() - (this._lastRender || 0);
        if (since >= PREVIEW_THROTTLE_MS) {
            this._lastRender = Date.now();
            this.refreshPreview();
            return;
        }
        clearTimeout(this._timer);
        this._timer = setTimeout(() => {
            this._lastRender = Date.now();
            this.refreshPreview();
        }, PREVIEW_THROTTLE_MS - since);
    }

    // --- progress ------------------------------------------------------------

    /**
     * How much of the document is legally complete, as a count and a bar.
     *
     * Counts only what the schema marks required, minus GST: that one is a
     * select with a value from the first paint, so counting it would start the
     * visitor at 1 of 6 for having done nothing. The line items count as one.
     */
    updateProgress() {
        const named = (name) => this.formTarget.querySelector(`[name='${name}']`);
        const filled = REQUIRED_FIELDS
            .map(named)
            .filter((el) => el && el.value.trim()).length;

        const done = filled + (this._hasCompleteRow() ? 1 : 0);
        const total = REQUIRED_FIELDS.length + 1;

        this.progressBarTarget.style.width = `${(done / total) * 100}%`;
        this.progressTextTarget.textContent = done === total
            ? "Ready to download"
            : `${done} of ${total} required fields filled`;
        this.element.classList.toggle("is-complete", done === total);
    }

    /** A line item only counts once it has all three of its values. */
    _hasCompleteRow() {
        return [...this.rowsTarget.querySelectorAll("[data-item-row]")].some((row) =>
            ["item_description", "item_quantity", "item_unit_price"].every(
                (name) => row.querySelector(`[name='${name}']`).value.trim()));
    }

    async refreshPreview() {
        // Renders overlap once they fire while typing, and responses can land
        // out of order. Only the newest may paint, or the document flickers
        // back to older content mid-keystroke.
        const ticket = (this._ticket || 0) + 1;
        this._ticket = ticket;

        let response;
        try {
            response = await fetch(this.previewUrlValue, {
                method: "POST",
                body: new FormData(this.formTarget),
                headers: { "X-Requested-With": "fetch" },
            });
        } catch (_error) {
            return;   // offline; keep the last good render on screen
        }
        if (!response.ok) return;

        const html = await response.text();
        if (ticket === this._ticket) this._render(html);
    }

    /**
     * Paint the document.
     *
     * The first render loads the whole page so the iframe gets the document's
     * <head> styles. Every render after that swaps only the <body>, because
     * reassigning srcdoc reloads the document and at typing speed that reads
     * as a flicker. Nothing in the head varies within a page, so there is
     * nothing up there to keep in step.
     */
    _render(html) {
        const doc = this.frameTarget.contentDocument;
        if (this._frameReady && doc && doc.body) {
            doc.body.innerHTML = new DOMParser()
                .parseFromString(html, "text/html").body.innerHTML;
            this._measurePaper();
            this.fitPreview();
            return;
        }

        // srcdoc parses asynchronously, so measuring straight after assigning
        // it would read the previous document's height.
        this.frameTarget.addEventListener("load", () => {
            this._frameReady = true;
            this._measurePaper();
            this.fitPreview();
        }, { once: true });
        this.frameTarget.srcdoc = html;
    }

    /** How tall the paper has to be to hold this document, and no taller. */
    _measurePaper() {
        const doc = this.frameTarget.contentDocument;
        if (!doc) return;
        const content = doc.documentElement.scrollHeight;
        this._paperHeight = Math.max(content, MIN_PAPER_PX);
        this.frameTarget.style.height = `${this._paperHeight}px`;
    }

    /** Fit the paper to the pane, and never blow it up past its true size. */
    fitPreview() {
        const available = this.paperTarget.clientWidth;
        if (!available || !this._paperHeight) return;
        // An A4 page rendered larger than A4 gains nothing and looks wrong, so
        // past that the paper just centres in the pane at 1:1.
        const scale = Math.min(available / A4_WIDTH_PX, 1);
        this.frameTarget.style.transform = `scale(${scale})`;
        this.stageTarget.style.width = `${A4_WIDTH_PX * scale}px`;
        this.stageTarget.style.height = `${this._paperHeight * scale}px`;
    }

    // --- line items --------------------------------------------------------

    addRow() {
        this.rowsTarget.appendChild(this.rowTemplateTarget.content.cloneNode(true));
        this.schedulePreview();
    }

    removeRow(event) {
        const rows = this.rowsTarget.querySelectorAll("[data-item-row]");
        // Always leave one row standing: an empty form is harder to recover
        // from than a blank row.
        if (rows.length > 1) {
            event.target.closest("[data-item-row]").remove();
        } else {
            rows[0].querySelectorAll("input").forEach((input) => { input.value = ""; });
        }
        this.schedulePreview();
    }

    // --- logo ----------------------------------------------------------------

    /** Read the chosen image into a data URI. It never becomes an upload. */
    pickLogo(event) {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (String(reader.result).length > MAX_LOGO_CHARS) {
                this._setLogoLabel("That image is too big. Try one under about 300KB.");
                return;
            }
            this.logoFieldTarget.value = reader.result;
            this._setLogoLabel(file.name);
            this.logoClearTarget.hidden = false;
            this.refreshPreview();
        };
        reader.onerror = () => this._setLogoLabel("That image would not load. Try another.");
        reader.readAsDataURL(file);
    }

    clearLogo() {
        this.logoFieldTarget.value = "";
        this.element.querySelector("[data-action*='pickLogo']").value = "";
        this._setLogoLabel("Add your logo");
        this.logoClearTarget.hidden = true;
        this.refreshPreview();
    }

    _setLogoLabel(text) {
        this.logoLabelTarget.textContent = text;
    }

    // --- after the download ----------------------------------------------

    /**
     * The PDF is on its way: say what a free account adds.
     *
     * `submit` fires only once the browser's own validation has passed, and the
     * response is an attachment, so the page stays put and this line is what
     * the visitor sees next. A server-side refusal re-renders the page, which
     * hides it again.
     */
    downloadStarted() {
        this.accountNoteTargets.forEach((note) => { note.hidden = false; });
    }

    // --- mobile pane switch ------------------------------------------------

    showPane(event) {
        this._showPane(event.currentTarget.dataset.pane);
    }

    /**
     * Bring Edit back when the browser refuses the download.
     *
     * A browser will not focus or flag a required field inside a hidden pane,
     * so pressing Download on the Preview tab with a field empty did nothing at
     * all. Showing Edit lets it point at the field it is complaining about.
     */
    revealInvalid() {
        const edit = this.paneTargets.find((el) => el.dataset.pane === "edit");
        if (edit && edit.hidden) this._showPane("edit");
    }

    _showPane(wanted) {
        this.paneTargets.forEach((el) => { el.hidden = el.dataset.pane !== wanted; });
        this.element.querySelectorAll("[data-pane-tab]").forEach((tab) => {
            tab.classList.toggle("is-active", tab.dataset.pane === wanted);
        });
        if (wanted === "preview") this.fitPreview();
    }

    // --- AI assistant ------------------------------------------------------

    /** Load a worked example into the box so they can watch it work first. */
    useExample(event) {
        this.aiInputTarget.value = event.currentTarget.dataset.example;
        this.aiInputTarget.focus();
    }

    aiKeydown(event) {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            this.askAi();
        }
    }

    async askAi() {
        const instruction = this.aiInputTarget.value.trim();
        if (!instruction || this.aiSendTarget.disabled) return;

        this._appendMessage("you", instruction);
        this.aiInputTarget.value = "";
        this._busy(true);

        try {
            const response = await fetch(this.aiUrlValue, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": this._csrfToken(),
                },
                body: JSON.stringify({
                    instruction,
                    session_id: this._session,
                    current: this._currentDocument(),
                    history: this._history,
                }),
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                this._appendMessage("chip", data.error
                    || "That did not go through. Try again in a moment.");
                return;
            }

            this._applyFields(data.fields || {});
            const reply = data.reply || "Done.";
            this._appendMessage("chip", reply);
            this._history.push({ role: "user", content: instruction });
            this._history.push({ role: "assistant", content: reply });
            this.refreshPreview();
        } catch (_error) {
            this._appendMessage("chip", "No connection. Your work is safe, try again.");
        } finally {
            this._busy(false);
        }
    }

    /**
     * Chip is working: the send button locks and a wave of dots stands in for
     * the reply that is coming, in the transcript where that reply will land.
     *
     * The dots are decorative, so the status line carries the same news in
     * words for a screen reader rather than leaving it to announce nothing.
     */
    _busy(busy) {
        this.aiSendTarget.disabled = busy;
        this.aiStatusTarget.textContent = busy ? "Chip is working on it" : "";

        if (!busy) {
            if (this._typing) this._typing.remove();
            this._typing = null;
            return;
        }

        const line = document.createElement("p");
        line.className = "ai-msg ai-msg--chip";
        const wave = document.createElement("span");
        wave.className = "ai-typing";
        wave.setAttribute("aria-hidden", "true");
        for (let i = 0; i < 3; i += 1) wave.appendChild(document.createElement("span"));
        line.appendChild(wave);

        this._typing = line;
        this.aiLogTarget.appendChild(line);
        this.aiLogTarget.hidden = false;
        this.aiLogTarget.scrollTop = this.aiLogTarget.scrollHeight;
    }

    _appendMessage(who, text) {
        // The dots are standing in for exactly this message, so they come out
        // before it goes in rather than in the `finally` after it.
        if (this._typing) {
            this._typing.remove();
            this._typing = null;
        }

        const line = document.createElement("p");
        line.className = `ai-msg ai-msg--${who}`;
        line.textContent = text;
        this.aiLogTarget.appendChild(line);
        this.aiLogTarget.hidden = false;
        this.aiLogTarget.scrollTop = this.aiLogTarget.scrollHeight;
    }

    /** The form's current contents, in the shape the assistant speaks. */
    _currentDocument() {
        const data = {};
        new FormData(this.formTarget).forEach((value, key) => {
            // `logo` is a data URI worth up to 300KB and the model has no use
            // for it, so it never goes near the request.
            if (key === "csrf_token" || key === "logo" || key.startsWith("item_")) return;
            if (value) data[key] = value;
        });
        data.line_items = [...this.rowsTarget.querySelectorAll("[data-item-row]")]
            .map((row) => ({
                description: row.querySelector("[name='item_description']").value,
                quantity: row.querySelector("[name='item_quantity']").value,
                unit_price: row.querySelector("[name='item_unit_price']").value,
            }))
            .filter((item) => item.description || item.quantity || item.unit_price);
        return data;
    }

    /** Write the assistant's invoice back into the form. */
    _applyFields(fields) {
        Object.entries(fields).forEach(([key, value]) => {
            if (key === "line_items") return;
            const input = this.formTarget.querySelector(`[name='${key}']`);
            if (!input || value === null || value === undefined) return;
            input.value = typeof value === "boolean" ? String(value) : value;
        });

        const items = fields.line_items;
        if (!Array.isArray(items) || items.length === 0) return;

        this.rowsTarget.innerHTML = "";
        items.forEach((item) => {
            this.rowsTarget.appendChild(this.rowTemplateTarget.content.cloneNode(true));
            const row = this.rowsTarget.lastElementChild;
            row.querySelector("[name='item_description']").value = item.description ?? "";
            row.querySelector("[name='item_quantity']").value = item.quantity ?? "";
            row.querySelector("[name='item_unit_price']").value = item.unit_price ?? "";
        });
    }

    _csrfToken() {
        const field = this.formTarget.querySelector("[name='csrf_token']");
        return field ? field.value : "";
    }
}
